import { Controller } from "@hotwired/stimulus"

const LAST_READ_STORAGE_KEY = "last_read_message"

// Manages footer-triggered utility panels such as the Comm Terminal.
export default class extends Controller {
  static identifier = "utility-panels"
  static targets = ["panel", "trigger", "wrapper", "pinnedPosition", "fabWrapper", "layoutToggle", "newMessageIndicator"]
  static values = {
    active: String,
    layout: { type: Boolean, default: false },
    hasNewMessage: { type: Boolean, default: false },
    lastMessage: Number,
    hideSystemMessages: { type: Boolean, default: false }
  }

  connect() {
    this.boundOutsideClick = this.handleOutsideClick.bind(this)
    this.boundDocumentKeydown = this.handleDocumentKeydown.bind(this)
    this.boundFrameLoad = this.handleFrameLoad.bind(this)
    this.boundTurboStream = this.handleTurboStream.bind(this)
    document.addEventListener("click", this.boundOutsideClick)
    document.addEventListener("keydown", this.boundDocumentKeydown)
    document.addEventListener("turbo:frame-load", this.boundFrameLoad)
    document.addEventListener("turbo:before-stream-render", this.boundTurboStream)

    this.layoutValue = this.restoreLayoutPreference("comm", false)

    this.latestMessageTimestamp = this.hasLastMessageValue ? this.parseTimestamp(this.lastMessageValue) : null

    this.moveChat()
    this.sync()
    this.initializeUnreadState()
  }

  disconnect() {
    document.removeEventListener("click", this.boundOutsideClick)
    document.removeEventListener("keydown", this.boundDocumentKeydown)
    document.removeEventListener("turbo:frame-load", this.boundFrameLoad)
    document.removeEventListener("turbo:before-stream-render", this.boundTurboStream)
  }

  activeValueChanged() {
    this.sync()
  }

  toggle(event) {
    event.preventDefault()
    const key = event.currentTarget.dataset.panelKey
    if (!key) return

    this.activeValue = this.activeValue === key ? "" : key
  }

  toggleLayout(event) {
    event.preventDefault()
    const panelKey = event.currentTarget.dataset.panelKey || "comm"

    if (panelKey === "comm") {
      this.layoutValue = !this.layoutValue
      this.persistLayoutPreference("comm", this.layoutValue)
      this.moveChat()
      if (this.layoutValue) {
        this.markMessagesRead()
      }
    }

    this.sync()
  }

  moveChat() {
    const chatFrame = document.getElementById("chat")
    if (!chatFrame) return

    if (this.layoutValue) {
      const pinnedContainer = document.getElementById("pinned-chat-container")
      if (pinnedContainer && !pinnedContainer.contains(chatFrame)) {
        pinnedContainer.appendChild(chatFrame)
      }
    } else {
      const fabContainer = document.getElementById("fab-chat-container")
      if (fabContainer && !fabContainer.contains(chatFrame)) {
        fabContainer.appendChild(chatFrame)
      }
    }
  }

  moveTutorial() {
    const tutorialPanel = document.getElementById("tutorial-panel")
    if (!tutorialPanel) return

    // Tutorial is always pinned - move to pinned container
    const pinnedContainer = document.getElementById("pinned-tutorial-container")
    if (pinnedContainer && !pinnedContainer.contains(tutorialPanel)) {
      pinnedContainer.appendChild(tutorialPanel)
    }
  }

  sync() {
    const active = this.activeValue
    const chatExists = Boolean(document.getElementById("chat"))
    const chatPinned = chatExists && this.layoutValue
    const tutorialExists = Boolean(document.getElementById("tutorial-panel"))
    const tutorialPinned = tutorialExists // Tutorial is always pinned
    const chatActive = this.isChatActive()
    const anyPinned = chatPinned || tutorialPinned

    if (chatActive && this.hasNewMessageValue) {
      this.setIndicator(false)
    }

    this.refreshUnreadIndicator()

    if (this.hasPinnedPositionTarget) {
      this.pinnedPositionTargets.forEach(pos => {
        pos.classList.toggle("hidden", !anyPinned)
      })
    }

    if (this.hasFabWrapperTarget) {
      this.fabWrapperTargets.forEach(wrapper => {
        const key = wrapper.dataset.panelKey
        const pinned = this.isPinned(key)
        wrapper.classList.toggle("hidden", pinned)
      })
    }

    if (active && this.isPinned(active)) {
      this.activeValue = ""
    }

    this.wrapperTargets.forEach(wrapper => {
      const key = wrapper.dataset.panelKey
      const isActive = key && key === active
      if (this.isPinned(key)) {
        wrapper.classList.remove("dropdown-open")
      } else {
        wrapper.classList.toggle("dropdown-open", isActive)
      }
    })

    this.panelTargets.forEach(panel => {
      const key = panel.dataset.panelKey
      const isActive = key && key === active
      const pinned = this.isPinned(key)

      if (!panel.classList.contains("dropdown-content")) {
        panel.classList.toggle("pointer-events-none", !isActive || pinned)
        panel.classList.toggle("opacity-0", !isActive || pinned)
        panel.classList.toggle("scale-95", !isActive || pinned)
        panel.classList.toggle("max-h-0", !isActive || pinned)
        panel.classList.toggle("overflow-hidden", !isActive || pinned)

        panel.classList.toggle("scale-100", isActive && !pinned)
        panel.classList.toggle("opacity-100", isActive && !pinned)
        panel.classList.toggle("max-h-[40rem]", isActive && !pinned)
        panel.classList.toggle("overflow-visible", isActive && !pinned)
      }

      if (isActive && !pinned) {
        // Removed auto-focus for chat input
      }
    })

    if (chatPinned) {
      // Removed auto-focus for pinned chat input
    }

    this.layoutToggleTargets.forEach(toggle => {
      const key = toggle.dataset.panelKey || "comm"
      const pinned = this.isPinned(key)
      toggle.classList.toggle("btn-active", pinned)
      toggle.setAttribute("aria-pressed", pinned ? "true" : "false")
      toggle.setAttribute("title", pinned ? "Unpin from Layout" : "Pin to Layout")
      const srOnlyLabel = toggle.querySelector(".sr-only")
      if (srOnlyLabel) {
        srOnlyLabel.textContent = pinned ? "Unpin from Layout" : "Pin to Layout"
      }

      const buttonText = pinned ? "[UNPIN]" : "[PIN]"
      const pinText = toggle.querySelector(".pin-text")
      if (pinText) {
        pinText.textContent = buttonText
      }
    })

    this.triggerTargets.forEach(trigger => {
      const key = trigger.dataset.panelKey
      const isActive = key && key === active
      const role = trigger.dataset.panelRole

      if (role === "tab") {
        if (isActive) {
          trigger.classList.add("opacity-0", "pointer-events-none", "translate-y-0")
          trigger.classList.remove("-translate-y-1/2", "hidden")
        } else {
          trigger.classList.remove("opacity-0", "pointer-events-none")
          trigger.classList.add("-translate-y-1/2")
          trigger.classList.remove("hidden")
        }
      } else {
        trigger.classList.toggle("btn-active", isActive)
      }

      if (trigger.hasAttribute("aria-pressed")) {
        trigger.setAttribute("aria-pressed", isActive ? "true" : "false")
      }

      if (trigger.hasAttribute("aria-expanded")) {
        trigger.setAttribute("aria-expanded", isActive ? "true" : "false")
      }
    })
  }

  handleOutsideClick(event) {
    if (!this.activeValue) return

    const activeWrapper = this.wrapperTargets.find(wrapper => {
      return wrapper.dataset.panelKey === this.activeValue
    })

    if (activeWrapper && activeWrapper.contains(event.target)) {
      return
    }

    this.activeValue = ""
  }
  
  handleFrameLoad(event) {
    if (event.target.id === "chat") {
      this.moveChat()
      this.sync()
      this.updateLastMessageFromDOM()
      this.refreshUnreadIndicator()
      this.applySystemMessageFilter()
      this.applyMutedWorkerFilter()
    }
  }

  handleTurboStream(event) {
    // Show a badge on the Comm trigger when chat Turbo Streams arrive while the panel is closed.
    const streamElement = event.target
    if (!streamElement || streamElement.tagName !== "TURBO-STREAM") return

    const target = streamElement.getAttribute("target")
    if (target === "chat-muted-worker-ids") {
      requestAnimationFrame(() => this.applyMutedWorkerFilter())
      return
    }
    if (target !== "chat-messages") return

    const action = streamElement.getAttribute("action") || "append"
    if (action !== "prepend" && action !== "append") return

    // Suppress muted operators
    const mutedWorkers = this.getMutedWorkerIdSet()
    if (mutedWorkers.size > 0) {
      const templateElement = streamElement.querySelector("template")
      if (templateElement && templateElement.content) {
        const messageElement = templateElement.content.querySelector('[data-role="chat-message"]')
        const workerId = messageElement?.getAttribute("data-worker-id")
        const normalizedWorkerId = this.normalizeWorkerId(workerId)
        if (normalizedWorkerId && mutedWorkers.has(normalizedWorkerId)) {
          event.preventDefault()
          return
        }
      }
    }

    // Suppress system messages if the preference is enabled
    if (this.hideSystemMessagesValue) {
      const templateElement = streamElement.querySelector("template")
      if (templateElement && templateElement.content) {
        const messageElement = templateElement.content.querySelector('[data-role="chat-message"]')
        if (messageElement && messageElement.getAttribute("data-is-system") === "true") {
          // Prevent rendering by removing the template content
          event.preventDefault()
          return
        }
      }
    }

    const incomingTimestamp = this.extractTimestampFromStream(streamElement)

    if (incomingTimestamp !== null) {
      this.updateLastMessageTimestamp(incomingTimestamp)
    } else {
      this.updateLastMessageFromDOM()
    }

    this.refreshUnreadIndicator()
  }

  handleDocumentKeydown(event) {
    if (event.key === "Escape" && this.activeValue) {
      this.activeValue = ""
    }
  }

  isChatActive() {
    return this.layoutValue || this.activeValue === "comm"
  }

  isPinned(key) {
    if (key === "comm") {
      return this.layoutValue && Boolean(document.getElementById("chat"))
    }
    if (key === "tutorial") {
      return Boolean(document.getElementById("tutorial-panel")) // Tutorial is always pinned
    }
    return false
  }

  hasNewMessageValueChanged() {
    this.updateNewMessageIndicator()
  }

  updateNewMessageIndicator(forceShow = this.hasNewMessageValue) {
    if (!this.hasNewMessageIndicatorTarget) return
    this.newMessageIndicatorTargets.forEach(indicator => {
      indicator.classList.toggle("hidden", !forceShow)
    })
  }

  restoreLayoutPreference(key, fallback) {
    if (key === "comm") {
      const storedCookie = this.getLayoutCookie(key)
      if (storedCookie !== null) return storedCookie === "true"
      return fallback
    }

    try {
      const stored = localStorage.getItem(`${key}_in_layout`)
      if (stored === null) return fallback
      return stored === "true"
    } catch (error) {
      return fallback
    }
  }

  persistLayoutPreference(key, value) {
    if (key === "comm") {
      this.persistLayoutCookie(key, value)
      return
    }

    // Non-"comm" panels use localStorage for layout preferences, matching restoreLayoutPreference
    try {
      localStorage.setItem(`${key}_in_layout`, value ? "true" : "false")
    } catch (error) {
      // If localStorage is unavailable, silently ignore to avoid breaking UI behavior
    }
  }

  getLatestMessageTimestamp() {
    const container = document.getElementById("chat-messages")
    if (!container) return null

    let latest = null

    container.querySelectorAll("[data-message-timestamp]").forEach(element => {
      latest = this.pickLatestTimestamp(latest, element.dataset?.messageTimestamp)
    })

    if (latest !== null) return latest

    container.querySelectorAll('[data-role="chat-message-timestamp"]').forEach(element => {
      latest = this.pickLatestTimestamp(latest, element.textContent)
    })

    return latest
  }

  pickLatestTimestamp(current, candidate) {
    const normalized = this.parseTimestamp(candidate)
    if (normalized === null) return current
    if (current === null || normalized > current) return normalized
    return current
  }

  parseTimestamp(value) {
    if (value === null || value === undefined) return null
    if (typeof value === "number") {
      return Number.isFinite(value) ? Math.trunc(value) : null
    }
    const parsed = parseInt(value, 10)
    return Number.isNaN(parsed) ? null : parsed
  }

  getLastReadTimestamp() {
    try {
      const stored = localStorage.getItem(LAST_READ_STORAGE_KEY)
      if (stored === null) return null
      return this.parseTimestamp(stored)
    } catch (error) {
      return null
    }
  }

  persistLastReadTimestamp(timestamp) {
    const normalized = this.parseTimestamp(timestamp)
    if (normalized === null) return
    try {
      localStorage.setItem(LAST_READ_STORAGE_KEY, normalized.toString())
    } catch (error) {
      // Ignore storage write issues (e.g., Safari private mode)
    }
  }

  extractTimestampFromStream(streamElement) {
    const templateElement = streamElement.querySelector("template")
    if (!templateElement || !templateElement.content) return null

    const attrCandidate = templateElement.content.querySelector("[data-message-timestamp]")
    if (attrCandidate) {
      const candidate = this.parseTimestamp(attrCandidate.dataset?.messageTimestamp)
      if (candidate !== null) return candidate
    }

    const hiddenCandidate = templateElement.content.querySelector('[data-role="chat-message-timestamp"]')
    if (hiddenCandidate) {
      return this.parseTimestamp(hiddenCandidate.textContent)
    }

    return null
  }

  updateLastMessageTimestamp(timestamp) {
    const normalized = this.parseTimestamp(timestamp)
    if (normalized === null) return null

    if (this.latestMessageTimestamp !== normalized) {
      this.latestMessageTimestamp = normalized
      if (!this.hasLastMessageValue || this.lastMessageValue !== normalized) {
        this.lastMessageValue = normalized
      }
    }

    return this.latestMessageTimestamp
  }

  ensureLatestMessageTimestamp() {
    return this.latestMessageTimestamp ?? this.updateLastMessageFromDOM()
  }

  updateLastMessageFromDOM() {
    const latest = this.getLatestMessageTimestamp()
    return latest === null ? null : this.updateLastMessageTimestamp(latest)
  }

  markMessagesRead(referenceTimestamp = this.ensureLatestMessageTimestamp()) {
    const timestamp = this.parseTimestamp(referenceTimestamp)
    if (timestamp === null) return

    this.updateLastMessageTimestamp(timestamp)
    this.persistLastReadTimestamp(timestamp)
    this.setIndicator(false)
  }

  shouldShowUnread(referenceTimestamp = this.ensureLatestMessageTimestamp()) {
    const timestamp = this.parseTimestamp(referenceTimestamp)
    if (timestamp === null) return false

    const lastRead = this.getLastReadTimestamp()
    if (lastRead === null) return true

    return lastRead < timestamp
  }

  setIndicator(shouldShow) {
    if (shouldShow !== this.hasNewMessageValue) {
      this.hasNewMessageValue = shouldShow
    } else {
      this.updateNewMessageIndicator(shouldShow)
    }
  }

  refreshUnreadIndicator() {
    const latest = this.ensureLatestMessageTimestamp()
    if (latest === null) {
      this.setIndicator(false)
      return
    }

    if (this.isChatActive()) {
      this.markMessagesRead(latest)
      return
    }

    this.setIndicator(this.shouldShowUnread(latest))
  }

  initializeUnreadState() {
    this.refreshUnreadIndicator()
  }

  applySystemMessageFilter() {
    if (!this.hideSystemMessagesValue) {
      // Show all system messages if preference is disabled
      const container = document.getElementById("chat-messages")
      if (container) {
        container.querySelectorAll('[data-role="chat-message"][data-is-system="true"]').forEach(el => {
          el.style.display = ""
        })
      }
      return
    }

    // Hide system messages when preference is enabled
    const container = document.getElementById("chat-messages")
    if (container) {
      container.querySelectorAll('[data-role="chat-message"][data-is-system="true"]').forEach(el => {
        el.style.display = "none"
      })
    }
  }

  applyMutedWorkerFilter() {
    const container = document.getElementById("chat-messages")
    if (!container) return

    const mutedWorkers = this.getMutedWorkerIdSet()

    container.querySelectorAll('[data-role="chat-message"][data-worker-id]').forEach(el => {
      const workerId = el.getAttribute("data-worker-id")
      if (!workerId) return
      const normalizedWorkerId = this.normalizeWorkerId(workerId)
      el.style.display = normalizedWorkerId && mutedWorkers.has(normalizedWorkerId) ? "none" : ""
    })
  }

  getMutedWorkerIdSet() {
    const candidates = Array.from(document.querySelectorAll("#chat-muted-worker-ids"))
    const sourceElement = candidates.length > 0 ? candidates[candidates.length - 1] : document.getElementById("chat-messages")
    const raw = sourceElement?.dataset?.mutedWorkerIds
    if (!raw) return new Set()

    return new Set(
      raw
        .split(",")
        .map(value => this.normalizeWorkerId(value))
        .filter(Boolean)
    )
  }

  normalizeWorkerId(value) {
    if (!value) return null
    const normalized = String(value).replace(/\D/g, "")
    return normalized.length > 0 ? normalized : null
  }

  persistLayoutCookie(key, value) {
    const normalized = value ? "true" : "false"
    const maxAge = 60 * 60 * 24 * 365
    const isHttps = window.location && window.location.protocol === "https:"
    const sameSite = isHttps ? "None" : "Lax"
    let cookie = `${encodeURIComponent(key)}_in_layout=${normalized}; path=/; max-age=${maxAge}; SameSite=${sameSite}`
    if (isHttps) cookie += "; Secure"
    document.cookie = cookie
  }

  getLayoutCookie(key) {
    const cookieName = `${encodeURIComponent(key)}_in_layout=`
    const cookies = document.cookie ? document.cookie.split(";") : []
    for (const cookie of cookies) {
      const trimmed = cookie.trim()
      if (trimmed.startsWith(cookieName)) {
        return trimmed.slice(cookieName.length)
      }
    }
    return null
  }

}
